import { Cancel, Build, CreateNewFolder, DriveFileMove, Edit, Delete } from '@mui/icons-material';
import {
  TableRow,
  alpha,
  TableCell,
  Box,
  InputBase,
  Tooltip,
  IconButton,
  Collapse,
  Table,
  TableBody,
} from '@mui/material';
import { useTheme } from '../../../contexts/ThemeContext';
import { useCallback } from 'react';
import { FolderIcon } from '../../../icons/folderIcon';
import FolderNameWithTooltip from './FolderNameWithTooltip';
import { FolderItemProps, AnyEvent } from './folderTypes';
import React from 'react';
import { PERMISSIONS, usePermissions } from '../../accessManager';

// Simple folder component without memo for clarity
function FolderItem({
  folder,
  depth,
  nestedItems,
  openRows,
  toggleFolder,
  isSelected,
  theme,
  editingFolderId,
  tempFolderName,
  inputRef,
  context,
  isMoveMode,
  currentFolder,
  onEditChange,
  onEditFinish,
  onEditCancel,
  onCreateFolder,
  onSetMoveMode,
  onEditStart,
  onDeleteClick,
  onCancelMoveMode,
  onEnteryMode,
  isViableTarget,
  moveTooltip,
  hasTableDescendants,
  folderType,
  currentUser,
}: FolderItemProps) {
  const isOpen = openRows[folder.folderid] || false;
  const { darkMode } = useTheme();
  const { hasAnyPermission } = usePermissions(currentUser);

  // Simple direct click handler for toggling
  const handleFolderClick = useCallback(
    (e: AnyEvent) => {
      e.stopPropagation();
      e.preventDefault();

      // Skip toggling if editing
      if (editingFolderId === folder.folderid) {
        // Don't do anything when editing
        return;
      }

      // Toggle this folder only
      toggleFolder(folder.folderid);
    },
    [editingFolderId, folder.folderid, toggleFolder]
  );

  // Stop propagation for action buttons
  const stopPropagation = (e: AnyEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <>
      {/* Folder row */}
      <TableRow
        onClick={handleFolderClick}
        sx={{
          cursor: 'pointer',
          transition: 'background-color 0.2s',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, darkMode ? 0.1 : 0.05),
          },
          '&:nth-of-type(even)': {
            backgroundColor: darkMode
              ? alpha(theme.palette.common.white, 0.02)
              : alpha(theme.palette.common.black, 0.02),
          },
          ...(isSelected && {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          }),
          ...(isMoveMode &&
            currentFolder?.folderid === folder.folderid && {
              backgroundColor: darkMode
                ? alpha(theme.palette.info.dark, 0.25)
                : alpha(theme.palette.info.light, 0.15),
              borderLeft: `4px solid ${theme.palette.info.main}`,
            }),
        }}
      >
        {/* Name column */}
        <TableCell
          sx={{
            textAlign: 'left',
            padding: 1,
            display: 'flex',
            alignItems: 'center',
            paddingLeft: `${depth * 10}px`,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            width: '70%',
            minWidth: '300px',
            overflow: 'hidden',
          }}
        >
          {isSelected && (
            <Box
              sx={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: theme.palette.primary.main,
                marginRight: 1,
                transition: 'all 0.2s ease',
              }}
              aria-hidden="true"
            />
          )}
          <Box
            sx={{
              marginRight: 1,
              color: isSelected ? theme.palette.primary.main : 'inherit',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <FolderIcon isOpen={isOpen} />
          </Box>
          {editingFolderId === folder.folderid ? (
            <InputBase
              inputRef={inputRef}
              value={tempFolderName}
              onChange={onEditChange}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  onEditFinish(e, folder);
                } else if (e.key === 'Escape') {
                  onEditCancel();
                }
                // Prevent event from bubbling up to parent handlers
                e.stopPropagation();
              }}
              autoFocus
              onClick={e => {
                // Ensure clicks on the input don't toggle the folder
                e.stopPropagation();
                e.preventDefault();
              }}
              sx={{
                flex: 1,
                padding: '4px 8px',
                fontSize: 'inherit',
                backgroundColor: theme.palette.background.default,
                color: theme.palette.text.primary,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                '& input': {
                  padding: '2px 4px',
                },
              }}
            />
          ) : (
            <FolderNameWithTooltip name={folder.name} isSelected={isSelected} theme={theme} />
          )}
        </TableCell>

        {/* Actions column */}
        <TableCell
          onClick={stopPropagation}
          sx={{
            textAlign: 'right',
            padding: 1,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            width: '30%',
            minWidth: '120px',
          }}
        >
          {isMoveMode && currentFolder?.folderid === folder.folderid && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              <Tooltip title="Cancel move operation" arrow>
                <IconButton
                  size="small"
                  onClick={e => {
                    stopPropagation(e);
                    onCancelMoveMode(e);
                  }}
                  sx={{
                    color: theme.palette.info.main,
                    '&:hover': {
                      color: theme.palette.info.dark,
                      backgroundColor: alpha(theme.palette.info.main, 0.1),
                    },
                  }}
                  aria-label="Cancel move operation"
                >
                  <Cancel fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {context === 'primary' &&
            hasAnyPermission([PERMISSIONS.CREATE_GLOBAL_ASSEMBLIES, PERMISSIONS.CREATE_ITEMS]) && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  '& .MuiIconButton-root': {
                    width: '32px',
                    height: '32px',
                  },
                }}
              >
                {/*Enter Assembly Mode*/}
                <Tooltip
                  title={folder.entitytype === 'assembly' ? 'Create Assembly' : 'Create Item'}
                  arrow
                >
                  <IconButton
                    size="small"
                    onClick={e => {
                      stopPropagation(e);
                      onEnteryMode(e, folder);
                    }}
                    sx={{
                      color: theme.palette.text.secondary,
                      '&:hover': {
                        color: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                  >
                    <Build fontSize="small" />
                  </IconButton>
                </Tooltip>

                {folder.level !== 5 && (
                  <Tooltip title="Create subfolder" arrow>
                    <IconButton
                      size="small"
                      onClick={e => {
                        stopPropagation(e);
                        onCreateFolder(e, folder);
                      }}
                      sx={{
                        color: theme.palette.text.secondary,
                        '&:hover': {
                          color: theme.palette.primary.main,
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        },
                      }}
                      aria-label={`Create subfolder in ${folder.name}`}
                    >
                      <CreateNewFolder fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                {folder.parentid && (
                  <Tooltip title={moveTooltip} arrow>
                    <IconButton
                      size="small"
                      onClick={e => {
                        stopPropagation(e);
                        onSetMoveMode(e, folder);
                      }}
                      disabled={isMoveMode && !isViableTarget}
                      sx={{
                        color: isMoveMode
                          ? theme.palette.success.main
                          : theme.palette.text.secondary,
                        '&:hover': {
                          color: isMoveMode
                            ? theme.palette.success.dark
                            : theme.palette.primary.main,
                          backgroundColor: isMoveMode
                            ? alpha(theme.palette.success.main, 0.1)
                            : alpha(theme.palette.primary.main, 0.1),
                        },
                        '&.Mui-disabled': {
                          color: alpha(theme.palette.text.disabled, 0.5),
                        },
                      }}
                      aria-label={
                        isMoveMode
                          ? `Set ${folder.name} as destination folder`
                          : `Move folder ${folder.name}`
                      }
                    >
                      <DriveFileMove fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip title="Rename folder" arrow>
                  <IconButton
                    size="small"
                    onClick={e => {
                      stopPropagation(e);
                      onEditStart(folder);
                    }}
                    sx={{
                      color: theme.palette.text.secondary,
                      '&:hover': {
                        color: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                    aria-label={`Rename folder ${folder.name}`}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip
                  title={
                    hasTableDescendants
                      ? `Cannot delete folder containing ${folderType === 'assembly' ? 'Assemblies' : 'Items'}`
                      : 'Delete folder and its contents'
                  }
                  arrow
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={e => {
                        stopPropagation(e);
                        onDeleteClick(e, folder);
                      }}
                      disabled={hasTableDescendants}
                      sx={{
                        color: theme.palette.text.secondary,
                        '&:hover': {
                          color: theme.palette.error.main,
                          backgroundColor: alpha(theme.palette.error.main, 0.1),
                        },
                        '&.Mui-disabled': {
                          color: alpha(theme.palette.text.disabled, 0.5),
                        },
                      }}
                      aria-label={`Delete folder ${folder.name}`}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            )}
        </TableCell>
      </TableRow>

      {/* Folder children (if open) */}
      {isOpen && (
        <TableRow>
          <TableCell colSpan={2} sx={{ padding: 0 }}>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  width: '100%',
                  backgroundColor: theme.palette.background.default,
                  paddingLeft: `${depth * 1}px`,
                }}
              >
                <Table sx={{ width: '100%' }}>
                  <TableBody>{nestedItems}</TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default React.memo(FolderItem);
